import React, { useEffect, useState } from 'react';
import { currency, banks } from '@utils';
import {
  FLink,
  Table,
  TableMobile,
  Button,
  LoadingIndicator,
  WideAlert,
  ComboBox,
  TextInput,
} from '@components';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Field, Formik } from 'formik';
import {
  CONFIRMACAO_ABSOLUTE_PATH,
  SIMULADOR_ABSOLUTE_PATH,
} from '../../../../common/utils/paths';
import { EmprestimoService } from '../../../../common/services/emprestimo';
import { RequerimentoService } from '../../../../common/services/requerimento';
import { dadosBancariosSchema } from '../../../../common/schemas/meus-dados/dados-bancarios';

export const Solicitacao = () => {
  const navigate = useNavigate();
  const { onBack, onNext } = useOutletContext();
  const [resultado, setResultado] = useState({});
  const [loading, setLoading] = useState(true);
  const [podeEnviar, setPodeEnviar] = useState(false);
  const { data: userData, dadosPessoais } = useSelector((state) => state.user);
  const { valor, parcelas } = useSelector((state) => state.loan.simulacao);
  const isLoading = useSelector(
    (state) => state.loan.limite.loading || state.loan.solicitacao.loading
  );
  const { saldoDevedor, taxa, taxaMes, taxaFundo } = useSelector(
    (state) => state.loan.limite
  );

  useEffect(() => {
    const service = new EmprestimoService();
    service
      .obterValoresEmprestimo({
        matricula: userData.matricula,
        token: userData.token,
        valor,
        parcelas,
      })
      .then((res) => {
        setResultado(res);
        setLoading(false);
      });
  }, [
    userData.matricula,
    parcelas,
    setLoading,
    setResultado,
    userData.token,
    valor,
  ]);

  useEffect(() => {
    const serviceReq = new RequerimentoService();

    const andamento = serviceReq.obterStatusRequerimento({
      matricula: userData.matricula,
      token: userData.token,
      status: 'em Andamento',
    });

    const pendente = serviceReq.obterStatusRequerimento({
      matricula: userData.matricula,
      token: userData.token,
      status: 'Pendente Correção',
    });

    Promise.all([andamento, pendente]).then((values) => {
      setPodeEnviar(
        !values[0].data.find(
          (form) => form.Formulario === 'SimulacaoEmprestimo'
        ) &&
          !values[1].data.find(
            (form) => form.Formulario === 'SimulacaoEmprestimo'
          )
      );
    });
  }, [userData.matricula, userData.token]);

  return isLoading || loading ? (
    <LoadingIndicator />
  ) : (
    <div className="card">
      <div className="card-body">
        <FLink
          className="justify-content-start mb-3 mb-md-32"
          iconLeft="icon-arrow_back"
          onClick={() => onBack(SIMULADOR_ABSOLUTE_PATH)}
        >
          voltar
        </FLink>
        <p className="text-muted ts-20 fw-bold mb-2">Resultado da Simulação</p>
        <p className="text-muted ts-14">
          Confira os dados da sua simulação antes de confirmar a solicitação.
        </p>
        <p className="text-muted ts-14">
          Taxa de juros de <b>{taxa.toFixed(2).replace('.', ',')}% a.a.</b>
          (equivalente a <b>{taxaMes.toFixed(4).replace('.', ',')}% a.m</b>) e
          taxa FQM de <b>{taxaFundo.toFixed(4).replace('.', ',')}% a.m </b>
          (Monitorado pelo Comitê de Investimento)
        </p>
        <p className="text-muted ts-14">
          Para efeito de previsão de prestação, foi considerado um IPCA
          hipotético de{' '}
          <b>
            {Number(resultado?.E_FATORCORR_SIMUL[0])
              .toFixed(2)
              .replace('.', ',')}
            %
          </b>
          , durante todo o prazo do contrato.
        </p>
        <br />
        {!!saldoDevedor && (
          <Table
            large
            className="d-none d-md-block"
            columns={['Saldo devedor']}
          >
            <tr>
              <td className="ts-20">{currency(saldoDevedor)}</td>
            </tr>
          </Table>
        )}
        <Table
          large
          className="d-none d-md-block"
          columns={[
            'Prazo',
            'IOF',
            'Taxa do fundo',
            'Valor solicitado',
            'Valor previsto para liberação',
            'Estimativa da primeira parcela',
            'Estimativa da segunda parcela',
          ]}
          tooltips={[
            'Tempo máximo escolhido para amortização',
            `Imposto de Operações Financeiras (Diário de ${Number(
              resultado?.E_PERC_IOF_DIAR[0]
            )
              .toFixed(5)
              .replace('.', ',')}% e adicional de ${Number(
              resultado?.E_PERC_IOF_ADIC[0]
            )
              .toFixed(2)
              .replace('.', ',')}%)`,
            `Valor a ser retido no desembolso para o Fundo de Quitação por Morte, equivalente a ${taxaFundo
              .toFixed(4)
              .replace('.', ',')}% (Monitorado pelo Comitê de Investimento)`,
            'Valor simulado para contratação',
            'Valor simulado para contratação, subtraído do Saldo Devedor, IOF e Taxa Fundo',
            'Sistema de amortização SAC pós-fixado. O valor da parcela sofrerá variações em função do IPCA. A primeira parcela poderá ser maior em função da data de crédito e a cobrança da 1ª parcela',
          ]}
        >
          <tr>
            <td className="ts-20">{parcelas} meses</td>
            <td className="ts-20">{currency(resultado?.E_VLRIOF_ACUM[0])}</td>
            <td className="ts-20">{currency(resultado?.E_VLRSEG_DESEMB[0])}</td>
            <td className="ts-20">{currency(valor)}</td>
            <td className="ts-20 fw-bold">
              {currency(resultado?.E_VLRLIQ[0])}
            </td>
            <td className="ts-20 fw-bold">
              {currency(resultado?.T_FLUXO[0].item[0].E_VLR_PARC[0])}
            </td>
            <td className="ts-20 fw-bold">
              {currency(resultado?.T_FLUXO[0].item[1].E_VLR_PARC[0])}
            </td>
          </tr>
          {/* {saldoDevedor && (
            <tr>
              <td colSpan={7} className="text-center fw-bold ts-18">
                Saldo devedor: {currency(saldoDevedor)}
              </td>
            </tr>
          )} */}
        </Table>
        <TableMobile className="mt-4">
          <div className="col-6">
            <div className="thead">Prazo</div>
            <div className="td">{parcelas} meses</div>
          </div>
          <div className="col-6">
            <div className="thead">IOF</div>
            <div className="td">{currency(resultado?.E_VLRIOF_ACUM[0])}</div>
          </div>
          <div className="col-6">
            <div className="thead">Taxa do fundo</div>
            <div className="td">{currency(resultado?.E_VLRSEG_DESEMB[0])}</div>
          </div>
          <div className="col-6">
            <div className="thead">Valor solicitado</div>
            <div className="td">{currency(valor)}</div>
          </div>
          <div className="col-6">
            <div className="thead">Valor previsto para liberação*</div>
            <div className="td">{currency(resultado?.E_VLRLIQ[0])}</div>
          </div>
          <div className="col-12">
            <div className="thead">Estimativa da primeira parcela**</div>
            <div className="td">
              {currency(resultado?.T_FLUXO[0].item[0].E_VLR_PARC[0])}
            </div>
          </div>
          <div className="col-12">
            <div className="thead">Estimativa da segunda parcela**</div>
            <div className="td">
              {currency(resultado?.T_FLUXO[0].item[1].E_VLR_PARC[0])}
            </div>
          </div>
          <div className="col-12">
            <div className="thead">Saldo devedor</div>
            <div className="td">{currency(saldoDevedor)}</div>
          </div>
        </TableMobile>
        <ul className="d-block d-md-none text-muted ts-14">
          <li className="mt-3">
            <b>Prazo</b> - Tempo máximo escolhido para amortização
          </li>
          <li className="mt-3">
            <b>IOF</b> - Imposto de Operações Financeiras (Diário de $
            {Number(resultado?.E_PERC_IOF_DIAR[0]).toFixed(5).replace('.', ',')}
            % e adicional de $
            {Number(resultado?.E_PERC_IOF_ADIC[0]).toFixed(2).replace('.', ',')}
            %)
          </li>
          <li className="mt-3">
            {' '}
            <b>Taxa do fundo</b> - Valor a ser retido no desembolso para o Fundo
            de Quitação por Morte, equivalente a $
            {taxaFundo.toFixed(4).replace('.', ',')}% (Monitorado pelo Comitê de
            Investimento)
          </li>
          <li className="mt-3">
            {' '}
            <b>Valor solicitado</b> - Valor simulado para contratação
          </li>
          <li className="mt-3">
            {' '}
            <b>Valor previsto para liberação</b> - Valor simulado para
            contratação, subtraído do Saldo Devedor, IOF e Taxa Fundo
          </li>
          <li className="mt-3">
            {' '}
            <b>Estimativa da primeira parcela</b> - Sistema de amortização SAC
            pós-fixado. O valor da parcela sofrerá variações em função do IPCA.
            A primeira parcela pode ser maior em função de 45 dias de juros
          </li>
        </ul>
      </div>
      <div className="card-footer">
        {podeEnviar ? (
          <div className="row">
            <h3 className="text-muted ts-20 fw-bold">
              Informe os dados da conta que deseja receber o empréstimo:
            </h3>
            <p className="text-muted ts-14 mt-2 mb-4">
              A conta a ser informada precisa obrigatoriamente ser conta
              corrente e pertencer ao titular do plano.
            </p>
            <Formik
              initialValues={{
                banco: '',
                agencia: '',
                conta: '',
                digito: '',
              }}
              validationSchema={dadosBancariosSchema}
              validateOnMount
            >
              {({ values, isValid }) => (
                <>
                  <form className="row">
                    <Field
                      name="banco"
                      component={ComboBox}
                      className="col-12 col-lg-6"
                      label="Qual é o seu banco?"
                      options={banks.map((s) => {
                        return {
                          value: s.codigo,
                          label: `${s.codigo} - ${s.banco}`,
                        };
                      })}
                    />
                    <Field
                      type="text"
                      name="agencia"
                      className="col-12 col-md-4 col-lg-2"
                      noPadding
                      component={TextInput}
                      label="Agência"
                    />
                    <Field
                      name="conta"
                      type="text"
                      className="col-12 col-md-6 col-lg-3"
                      component={TextInput}
                      label="Nº da Conta Corrente"
                    />
                    <Field
                      name="digito"
                      type="text"
                      className="col-12 col-md-2 col-lg-1"
                      noPadding
                      component={TextInput}
                      label="Dígito"
                    />
                  </form>
                  {dadosPessoais.statusPlano === 'Ativo' ? (
                    <p className="mt-3 ts-14 text-red">
                      Caso tenha um requerimento de resgate parcial ainda não
                      pago, a alteração dos dados bancários acima irá alterar a
                      conta para pagamento do resgate. Essa conta também será
                      utilizada para a cobrança, via débito automático, das
                      parcelas de empréstimos em atraso nos bancos conveniados à
                      Forluz.
                    </p>
                  ) : (
                    <p className="mt-3 ts-14 text-red">
                      Essa conta também será utilizada para a cobrança, via
                      débito automático, das parcelas de empréstimos em atraso
                      nos bancos conveniados à Forluz.
                    </p>
                  )}

                  <hr className="my-4" />
                  <div className="d-block d-md-flex align-items-center ms-auto col-12 col-md-8 col-lg-6">
                    <Button
                      className="mb-3 mb-md-0"
                      transparent
                      color="orange"
                      onClick={() => onBack(SIMULADOR_ABSOLUTE_PATH)}
                    >
                      Simular outro valor
                    </Button>
                    <Button
                      disabled={!podeEnviar || !isValid}
                      className="ms-0 ms-md-3"
                      onClick={() =>
                        onNext(
                          CONFIRMACAO_ABSOLUTE_PATH,
                          {
                            vencPrimeira:
                              resultado?.T_FLUXO[0].item[0].DT_VECTO[0],
                            vencUltima:
                              resultado?.T_FLUXO[0].item.pop().DT_VECTO[0],
                          },
                          values
                        )
                      }
                    >
                      Solicitar empréstimo
                    </Button>
                  </div>
                </>
              )}
            </Formik>
          </div>
        ) : (
          <div>
            <WideAlert className="mb-4" iconClear={false}>
              <div className="d-block">
                <p className="ts-16">
                  <strong>Aviso importante:</strong> Você já possui uma
                  solicitação de empréstimo em andamento. Caso queira dar
                  andamento em uma nova contratação de empréstimo, favor entrar
                  em contato com a Forluz.
                </p>

                <FLink
                  className="justify-content-start mt-2 col-12"
                  onClick={() => navigate('/requerimentos/andamento/*')}
                >
                  Visualizar solicitação em andamento
                </FLink>
              </div>
            </WideAlert>
          </div>
        )}
      </div>
    </div>
  );
};
